import { hashFunction } from './hashFunction';
import { FR_CA_LOCALE, DATE_TIME_TWO_DIGIT_FORMAT, DATE_TIME_NUMERIC_FORMAT, ESTIMATE_STATUS_FORMATS, EN_US_LOCALE } from './Constants';
export class Utilities {
    /**
     * @description pass this to an input to highlight text
     * @param event eg. onFocus, onBlur
     */
    static selectAllText(event) {
        event && event.currentTarget && event.currentTarget.select();
    }
    /**
     * @description format number to include currency symbol and decimals
     * @param price number to format
     * @param currencyCode eg. 'USD' for $x.xx format
     * @param locale eg. 'en-US' for language specific format
     */
    static formatPrice(price, currencyCode = 'USD', locale) {
        const currentLocale = Utilities.getLocaleForMoment(locale);
        return price.toLocaleString(currentLocale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
    /**
     * @description format number to include currency symbol, decimals, spaces and comma
     * @param number number to format
     */
    static formatCurrency(value) {
        const locale = Utilities.getLocaleForMoment();
        let formattedValue = '';
        if (locale.includes('CA')) {
            formattedValue = value.endsWith('$') ? value.slice(0, value.length - 1) : value;
            formattedValue = formattedValue.replace(/\s/g, '').replace(/,/g, '.');
        }
        else {
            formattedValue = value.startsWith('$')
                ? value.slice(1).replace(/,/g, '')
                : value.replace(/,/g, '');
        }
        if (!isNaN(Number(formattedValue))) {
            const decimalIndex = formattedValue.indexOf('.');
            const decimalDigit = decimalIndex === -1 ? 0 : formattedValue.length - decimalIndex - 1;
            return Utilities.formatPriceWithoutCurrencyCode(Number(formattedValue), decimalDigit, decimalDigit);
        }
        return '';
    }
    /**
     * NOTE: For minimal risk, this function was added shortly before 22.5 to be merged back. In the future, this function's
     * logic could be merged with formatPrice function. In browser, only supporting French (Canada), not French (France)
     * REF: https://www.w3schools.com/jsref/jsref_tolocalestring_number.asp
     * @description format number to include currency symbol without currencyCode and decimals.
     * @param price number to format
     */
    static formatPriceWithoutCurrencyCode(price, minimumFractionDigits = 2, maximumFractionDigits = 2) {
        const locale = Utilities.getLocaleForMoment();
        const currencyCode = locale.includes('CA') ? 'CAD' : 'USD';
        return price.toLocaleString(locale, {
            style: 'currency',
            currency: currencyCode,
            minimumFractionDigits: minimumFractionDigits,
            maximumFractionDigits: maximumFractionDigits
        });
    }
    /**
     * @description format number to include % symbol and decimals
     * @param number number to format ex 0.15
     * @param minimumFractionDigits minimum number of decimal digits
     */
    static formatPercent(number, minimumFractionDigits = 0) {
        return number.toLocaleString(undefined, {
            style: 'percent',
            minimumFractionDigits: minimumFractionDigits
        });
    }
    /**
     * @description format phone number to standard 10 digit format
     * @param price number to format
     */
    static formatPhone(phone) {
        if (!phone)
            return '';
        const cleaned = phone.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
        return match !== null ? `(${match[1]}) ${match[2]}-${match[3]}` : phone;
    }
    /**
     * @description format number as per the selected locale
     * @param value number for format
     */
    static formatNumber(value, minimumFractionDigits = 1, maximumFractionDigits = 1, locale, useGrouping) {
        const currentLocale = Utilities.getLocaleForMoment(locale);
        const number = value || 0;
        return number.toLocaleString(currentLocale, {
            minimumFractionDigits,
            maximumFractionDigits,
            useGrouping: useGrouping === undefined ? true : useGrouping
        });
    }
    /**
     * @description validate zip/postal code
     * @param price code to validate
     */
    static validateZipCode(zipCode) {
        const CAPostalCode = new RegExp(/(^[ABCEGHJKLMNPRSTVXY]{1}\d{1}[A-Z]{1} *\d{1}[A-Z]{1}\d{1}$)/);
        const USZipCode = new RegExp(/(^\d{5}$)|(^\d{5}-\d{4}$)/);
        if (USZipCode.test(zipCode)) {
            return 'US';
        }
        else if (CAPostalCode.test(zipCode)) {
            return 'CA';
        }
        else {
            return false;
        }
    }
    /**
     * @description Sorts an array of object<T> in the order specified or ascending by default.
     *              If key is undefined, throw error. Null is treated as negative infinity.
     * @param collection list of generic objects
     * @param key object key to sort by
     * @param isAscending optionally reverse the order of the sort
     * @param comparator optionally send in custom comparator for comparision
     */
    static sortByKey(collection, key, isAscending = true, comparator) {
        const sortMethod = (a, b) => {
            if (a[key] === undefined || b[key] === undefined)
                throw new Error(`Key "${String(key)}" is undefined.`);
            return this.compare(a[key], b[key]);
        };
        const modifySort = (a, b) => {
            const sortValue = comparator ? comparator(a[key], b[key]) : sortMethod(a, b);
            return isAscending ? sortValue : -sortValue;
        };
        return collection.sort(modifySort);
    }
    /**
     * @description Compares two string | number | null and returns number
     * 				0 if equal
     * 				positive value if a is greater than b
     * 				negative value if b is greater than a
     * @param a Value to be compared
     * @param b Value to be compared
     */
    static compare(a, b) {
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }
        if (typeof a === 'string' && typeof b === 'string') {
            a = a.toLowerCase();
            b = b.toLowerCase();
        }
        if (a === b)
            return 0;
        if (a === null)
            return -1;
        if (b === null)
            return 1;
        if (a > b)
            return 1;
        return -1;
    }
    /**
     * @description Generates a unique hash code from an object.
     * @param obj Any object, T can be used to specify the type.
     * Object types can create circular depedencies, return a label instead.
     * See https://stackoverflow.com/a/46908358
     */
    static generateHashCodeFromObject(obj) {
        return hashFunction(JSON.stringify(obj, (key, value) => {
            if (key && typeof value === 'object') {
                return '[object]';
            }
            else {
                return value;
            }
        }));
    }
    /**
     * @description Returns true if the browser is IE11
     */
    static isIE11() {
        return document['documentMode'] === 11;
    }
    /**
     * @description Returns kilometers for given miles input
     * @param miles number
     */
    static convertMilesToKilometers(miles) {
        return miles * 1.609344;
    }
    /**
     * @description return the region and language from language culture.
     * @param languageCulture the language culture to be converted to region and culture.
     */
    static getLanguageandRegion(languageCulture) {
        const lngCulture = languageCulture.split('-');
        const language = lngCulture.length == 2 ? lngCulture[0] : 'en';
        const region = lngCulture.length == 2 ? lngCulture[1] : 'us';
        return [language, region];
    }
    /**
     * @description Round a number to given precision
     * @param number The number for round.
     * @param precision precision value for round
     */
    static round(number, precision) {
        const factor = Math.pow(10, precision);
        const tempNumber = number * factor;
        return Math.round(tempNumber) / factor;
    }
    /**
     * @description return current locale
     */
    static getLocale() {
        return ((window.navigator.languages && window.navigator.languages[0]) ||
            window.navigator.language ||
            window.navigator.userLanguage ||
            window.navigator.browserLanguage ||
            window.navigator.systemLanguage);
    }
    /**
     *
     * @summary return locale for moment
     * @returns 'fr-CA' for french languages
     * @default 'en-US' for other languages
     */
    static getLocaleForMoment(locale) {
        const frenchLanguageRegex = new RegExp(/^fr/);
        const currentLocale = locale || this.getLocale();
        return frenchLanguageRegex.test(currentLocale) ? FR_CA_LOCALE : EN_US_LOCALE;
    }
    /**
     * @description return time format options as per current locale
     */
    static getTimeFormatOptions() {
        return this.getLocale() === FR_CA_LOCALE
            ? {
                hour: DATE_TIME_TWO_DIGIT_FORMAT,
                minute: DATE_TIME_TWO_DIGIT_FORMAT,
                second: DATE_TIME_TWO_DIGIT_FORMAT,
                hour12: false
            }
            : {};
    }
    /**
     * @description return date format options as per current locale
     */
    static getDateFormatOptions() {
        return this.getLocale() === FR_CA_LOCALE
            ? {
                year: DATE_TIME_NUMERIC_FORMAT,
                month: DATE_TIME_TWO_DIGIT_FORMAT,
                day: DATE_TIME_TWO_DIGIT_FORMAT
            }
            : {};
    }
    static formatEstimateStatus(estimateStatus) {
        const estimateStatusFormatted = ESTIMATE_STATUS_FORMATS[estimateStatus];
        return estimateStatusFormatted ? estimateStatusFormatted : estimateStatus;
    }
}
/**
 * @description Converts a number into bytes, kilobytes, megabytes, gigabytes, or terabytes
 * depending on the size.
 * @param fileSizeInBytes The file size in bytes that is to be converted.
 */
Utilities.convertFileSizeToString = (fileSizeInBytes) => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (fileSizeInBytes === 0)
        return '0 Byte';
    const i = Math.floor(Math.log(fileSizeInBytes) / Math.log(1024));
    return parseFloat((fileSizeInBytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
};
//# sourceMappingURL=Utilities.js.map